import { findDatePresetOption, getEffectiveSlicerPanel } from '../runtime'
import { resolveDefaultDateSortKey, resolveWidgetSortKey } from '../sortUtils'
import type {
  DashboardDefinition,
  DashboardRuntimeDateRange,
  DashboardWidget,
  DatePresetOption,
  SavedDashboardViewState,
  SavedDashboardViewWidgetState,
} from '../types'
import {
  createDashboardGlobalFilterState,
  createDefaultWidgetQueryState,
  type DashboardGlobalFilterState,
  type DashboardViewState,
  type WidgetQueryState,
} from './engine/state'

export const VIEW_SCHEMA_VERSION = 1
export const MAX_SAVED_VIEWS_PER_DASHBOARD = 15
export const MAX_VIEW_NAME_LENGTH = 80

export const SKIPPED_VIEW_SETTINGS_MESSAGE = 'Some saved settings no longer apply to this dashboard and were skipped.'
export const UNSUPPORTED_VIEW_MESSAGE = 'This view needs a newer version of the app. Refresh the page.'

export type ReconciledViewState = {
  filters: DashboardGlobalFilterState
  widgetQueries: Record<string, WidgetQueryState>
}

export type ReconcileViewResult = {
  state: ReconciledViewState
  /** Saved parts that no longer apply, e.g. "slicer:Client Manager" or "widget:wip-by-client". */
  skipped: string[]
  /** The view was written by a newer schema; the dashboard defaults are returned instead. */
  unsupported: boolean
}

// Page is never saved: a view opens on each widget's first page.
const SAVED_WIDGET_FIELDS = [
  'pageSize',
  'sortColumn',
  'sortDirection',
  'groupByDimension',
  'groupByDimensions',
  'drilldown',
  'measureParameterSelections',
] as const satisfies ReadonlyArray<keyof SavedDashboardViewWidgetState & keyof WidgetQueryState>

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

// Date.parse rolls impossible days over ("2026-02-31" becomes 3 March), so the date must read back unchanged.
function isValidIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) return false
  const time = Date.parse(value)
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value
}

function sameValue(left: unknown, right: unknown) {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null)
}

// A preset stays relative ("Last 30 days"), so its resolved window is not part of the view.
function toSavedDateRange(dateRange: DashboardRuntimeDateRange): DashboardRuntimeDateRange {
  return dateRange.mode === 'preset'
    ? { mode: 'preset', preset: dateRange.preset, from: null, to: null }
    : { mode: dateRange.mode, preset: null, from: dateRange.from, to: dateRange.to }
}

function sortedRecord<T>(record: Record<string, T>): Record<string, T> {
  return Object.fromEntries(Object.keys(record).sort().map((key) => [key, record[key] as T]))
}

/** The view for a set of filters and widget queries: the applied filters, plus only what each widget changed. */
export function serializeViewParts(
  dashboard: DashboardDefinition,
  filters: DashboardGlobalFilterState,
  widgetQueries: Record<string, WidgetQueryState>,
): SavedDashboardViewState {
  const widgets: Record<string, SavedDashboardViewWidgetState> = {}
  for (const widget of dashboard.widgets) {
    const query = widgetQueries[widget.id]
    if (!query) continue

    const defaults = createDefaultWidgetQueryState(widget)
    const changed: SavedDashboardViewWidgetState = {}
    for (const field of SAVED_WIDGET_FIELDS) {
      if (!sameValue(query[field], defaults[field])) {
        Object.assign(changed, { [field]: query[field] })
      }
    }
    if (Object.keys(changed).length > 0) {
      widgets[widget.id] = changed
    }
  }

  return {
    schemaVersion: VIEW_SCHEMA_VERSION,
    filters: {
      slicerValues: sortedRecord(filters.slicerValues),
      dateRange: toSavedDateRange(filters.dateRange),
    },
    widgets,
  }
}

export function serializeViewState(state: DashboardViewState): SavedDashboardViewState | null {
  if (!state.dashboard) return null
  return serializeViewParts(
    state.dashboard,
    state.appliedFilters,
    Object.fromEntries(Object.entries(state.widgets).map(([widgetId, session]) => [widgetId, session.query])),
  )
}

export function areViewStatesEqual(left: SavedDashboardViewState | null, right: SavedDashboardViewState | null) {
  return JSON.stringify(left) === JSON.stringify(right)
}

function isKnownSortColumn(widget: DashboardWidget, column: string) {
  // resolveWidgetSortKey maps a dimension or measure label to its field and passes anything else through.
  if (resolveWidgetSortKey(widget, column) !== column) return true
  const known = [
    ...(widget.dimensions ?? []).map((field) => field.value),
    ...(widget.measures ?? []).map((field) => field.value),
    ...(widget.measureParameters ?? []).flatMap((parameter) => parameter.options.map((option) => option.measure)),
    resolveDefaultDateSortKey(widget),
  ]
  return known.includes(column)
}

function reconcileSlicerValue(
  selection: 'single' | 'multi' | undefined,
  value: unknown,
): { ok: true; value: string | string[] | null } | { ok: false } {
  if (value === null) return { ok: true, value: null }
  const values = Array.isArray(value) ? value : [value]
  if (!values.every((item) => typeof item === 'string')) return { ok: false }
  if (selection === 'single') {
    return values.length <= 1 ? { ok: true, value: (values[0] as string | undefined) ?? null } : { ok: false }
  }
  return { ok: true, value: values.length > 0 ? (values as string[]) : null }
}

function reconcileDateRange(
  saved: DashboardRuntimeDateRange,
  datePresets: DatePresetOption[] | null | undefined,
): DashboardRuntimeDateRange | null {
  if (saved.mode === 'none') return { mode: 'none', preset: null, from: null, to: null }
  if (saved.mode === 'preset') {
    return findDatePresetOption(datePresets, saved.preset)
      ? { mode: 'preset', preset: saved.preset, from: null, to: null }
      : null
  }
  if (saved.mode === 'custom') {
    // The API drops null fields when it stores a view, so a missing end is an open one.
    const from = saved.from ?? null
    const to = saved.to ?? null
    const fromOk = from === null || isValidIsoDate(from)
    const toOk = to === null || isValidIsoDate(to)
    return fromOk && toOk ? { mode: 'custom', preset: null, from, to } : null
  }
  return null
}

function reconcileWidgetQuery(
  widget: DashboardWidget,
  saved: SavedDashboardViewWidgetState,
  skip: (part: string) => void,
): WidgetQueryState {
  const query = createDefaultWidgetQueryState(widget)

  if (typeof saved.pageSize === 'number' && Number.isInteger(saved.pageSize) && saved.pageSize > 0) {
    query.pageSize = saved.pageSize
  }

  if ('sortColumn' in saved) {
    if (saved.sortColumn === null || (typeof saved.sortColumn === 'string' && isKnownSortColumn(widget, saved.sortColumn))) {
      query.sortColumn = saved.sortColumn ?? null
      if (saved.sortDirection === 'asc' || saved.sortDirection === 'desc') query.sortDirection = saved.sortDirection
    } else {
      skip(`sort:${widget.id}`)
    }
  } else if (saved.sortDirection === 'asc' || saved.sortDirection === 'desc') {
    query.sortDirection = saved.sortDirection
  }

  if ('groupByDimension' in saved || 'groupByDimensions' in saved) {
    const dimensions = saved.groupByDimensions
    if (dimensions === null || dimensions === undefined || (Array.isArray(dimensions) && dimensions.every((item) => typeof item === 'string'))) {
      query.groupByDimensions = dimensions ?? null
      query.groupByDimension = typeof saved.groupByDimension === 'string' ? saved.groupByDimension : dimensions?.[0] ?? null
    } else {
      skip(`groupBy:${widget.id}`)
    }
  }

  if (saved.drilldown) {
    const { field, value, dimension } = saved.drilldown
    if (typeof field === 'string' && field && typeof value === 'string' && typeof dimension === 'string' && dimension) {
      query.drilldown = { field, value, dimension }
    } else {
      skip(`drilldown:${widget.id}`)
    }
  }

  if (saved.measureParameterSelections) {
    const selections = { ...(query.measureParameterSelections ?? {}) }
    for (const [parameterId, optionId] of Object.entries(saved.measureParameterSelections)) {
      const parameter = widget.measureParameters?.find((item) => item.id === parameterId)
      if (parameter?.options.some((option) => option.id === optionId)) {
        selections[parameterId] = optionId
      } else {
        skip(`measure:${widget.id}:${parameterId}`)
      }
    }
    query.measureParameterSelections = Object.keys(selections).length > 0 ? selections : null
  }

  return query
}

/**
 * Lays a saved view over the current dashboard's defaults, keeping only what still applies.
 * Never throws: anything that no longer fits is left at its default and listed in `skipped`.
 */
export function reconcileViewState(
  view: SavedDashboardViewState | null,
  dashboard: DashboardDefinition,
  datePresets: DatePresetOption[] | null | undefined,
): ReconcileViewResult {
  const defaults = createDashboardGlobalFilterState(dashboard, datePresets)
  const widgetQueries = Object.fromEntries(
    dashboard.widgets.map((widget) => [widget.id, createDefaultWidgetQueryState(widget)]),
  )
  const result = (filters: DashboardGlobalFilterState, skipped: string[], unsupported = false): ReconcileViewResult => ({
    state: { filters, widgetQueries },
    skipped,
    unsupported,
  })

  if (!view) return result(defaults, [])
  if ((view.schemaVersion ?? VIEW_SCHEMA_VERSION) > VIEW_SCHEMA_VERSION) return result(defaults, [], true)

  const skipped: string[] = []
  const skip = (part: string) => skipped.push(part)
  const slicerPanel = getEffectiveSlicerPanel(dashboard)

  const slicerValues = { ...defaults.slicerValues }
  for (const [field, value] of Object.entries(view.filters?.slicerValues ?? {})) {
    const slicer = slicerPanel?.slicers.find((item) => item.field === field)
    const reconciled = slicer ? reconcileSlicerValue(slicer.selection, value) : { ok: false as const }
    if (reconciled.ok) {
      slicerValues[field] = reconciled.value
    } else {
      skip(`slicer:${field}`)
    }
  }

  let dateRange = defaults.dateRange
  const savedDateRange = view.filters?.dateRange
  if (savedDateRange) {
    const reconciled = slicerPanel?.dateRange ? reconcileDateRange(savedDateRange, datePresets) : null
    if (reconciled) {
      dateRange = reconciled
    } else {
      skip('dateRange')
    }
  }

  for (const [widgetId, saved] of Object.entries(view.widgets ?? {})) {
    const widget = dashboard.widgets.find((item) => item.id === widgetId)
    if (!widget || !saved || typeof saved !== 'object') {
      skip(`widget:${widgetId}`)
      continue
    }
    widgetQueries[widgetId] = reconcileWidgetQuery(widget, saved, skip)
  }

  return result({ slicerValues, dateRange }, skipped)
}

function describeSlicerValue(value: string | string[] | null) {
  if (value === null) return 'All'
  return Array.isArray(value) ? value.join(', ') : value
}

/** One line per saved setting, for the "This view saves" summary. */
export function describeViewState(
  view: SavedDashboardViewState,
  dashboard: DashboardDefinition,
  datePresets: DatePresetOption[] | null | undefined,
): string[] {
  const lines: string[] = []
  const slicerPanel = getEffectiveSlicerPanel(dashboard)
  const defaults = createDashboardGlobalFilterState(dashboard, datePresets)

  const dateRange = view.filters?.dateRange
  if (slicerPanel?.dateRange && dateRange) {
    if (dateRange.mode === 'preset') {
      const label = findDatePresetOption(datePresets, dateRange.preset)?.label ?? dateRange.preset
      lines.push(`Date range: ${label} (stays relative)`)
    } else if (dateRange.mode === 'custom') {
      lines.push(`Date range: ${dateRange.from ?? '…'} to ${dateRange.to ?? '…'}`)
    } else {
      lines.push('Date range: no date filter')
    }
  }

  for (const slicer of slicerPanel?.slicers ?? []) {
    const value = view.filters?.slicerValues?.[slicer.field] ?? null
    if (!sameValue(value, defaults.slicerValues[slicer.field])) {
      lines.push(`${slicer.label}: ${describeSlicerValue(value)}`)
    }
  }

  for (const [widgetId, saved] of Object.entries(view.widgets ?? {})) {
    const widget = dashboard.widgets.find((item) => item.id === widgetId)
    if (!widget) continue
    const parts: string[] = []
    if (saved.sortColumn) {
      parts.push(`sorted by ${saved.sortColumn}, ${saved.sortDirection === 'asc' ? 'low to high' : 'high to low'}`)
    }
    if (saved.groupByDimensions?.length) {
      parts.push(`grouped by ${saved.groupByDimensions.join(', ')}`)
    }
    if (saved.drilldown) {
      parts.push(`drilled into ${saved.drilldown.value}`)
    }
    if (saved.measureParameterSelections) {
      for (const [parameterId, optionId] of Object.entries(saved.measureParameterSelections)) {
        const parameter = widget.measureParameters?.find((item) => item.id === parameterId)
        const option = parameter?.options.find((item) => item.id === optionId)
        if (parameter && option) parts.push(`${parameter.label}: ${option.label}`)
      }
    }
    if (parts.length > 0) {
      lines.push(`${widget.title || 'Widget'}: ${parts.join('; ')}`)
    }
  }

  return lines
}
